import React, { useState, useMemo } from "react";
import { toast } from "react-toastify";
import useSWR from "swr";
import moment from "moment";
import { useNavigate } from "react-router-dom";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import SuperAdminLayout from "../../layouts/SuperAdminLayout";
import Loader from "../../components/Loader";
import CustomButton from "../../components/CustomButton";
import {
  deleteBriefAPI,
  downloadPlan,
  getBriefListAPI,
  getPlansByBriefIdAPI,
} from "../../apis/briefs.apis";
import BriefStatusTag from "../../components/BriefStatusTag";
import roles from "../../constants/roles";
import useAuth from "../../hooks/useAuth";
import { mapPlanCSVDownload, convertToCSV } from "../../utils/helper.utils";

import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";

const ALLOWED_ROLES_TO_DOWNLOAD_BRIEF_PPT = [
  roles.SUPERADMIN,
  roles.CONTROLLER,
];

function CountToolTip({ counts }) {
  return (
    <Tooltip title={counts.join(", ")} placement="right">
      <Typography
        sx={{
          textDecoration: "underline",
          cursor: "pointer",
          color: "blue",
        }}
      >
        {counts.length}
      </Typography>
    </Tooltip>
  );
}

function TableHeadCell(params) {
  return <TableCell sx={{ fontWeight: "600" }} {...params} />;
}

const BriefList = () => {
  const [isLoaderOpen, setisLoaderOpen] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const navigate = useNavigate();
  const { data, error, isLoading, mutate } = useSWR(
    "/briefs/briefs",
    getBriefListAPI
  );

  const { role_id } = useAuth();

  const isAllowedToDownload =
    ALLOWED_ROLES_TO_DOWNLOAD_BRIEF_PPT.includes(role_id);

  const handleDelete = (brief_id) => {
    if (!window.confirm("Are you sure you want to delete this?")) {
      return;
    }

    setisLoaderOpen(true);
    deleteBriefAPI(brief_id)
      .then(() => {
        mutate();
        toast.success("Deleted successfully!");
      })
      .catch((e) => {
        let msg = "Something went wrong!";

        if (e.response && e.response?.data?.message) {
          msg = e.response?.data?.message;
        }

        toast.error(msg);
      })
      .finally(() => {
        setisLoaderOpen(false);
      });
  };

  const handleEdit = (brief_id) => {
    navigate("/edit-brief/" + brief_id);
  };

  const handleDownloadPlan = (brief_id) => {
    setisLoaderOpen(true);

    downloadPlan(brief_id)
      .then((v) => {})
      .catch((e) => {
        const msg = e?.response?.data?.message || "Something went wrong!";
        toast.error(msg);
      })
      .finally((v) => {
        setisLoaderOpen(false);
      });
  };

  const handleDownloadAllCSV = () => {
    if (!data || data.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const headers = columns
      .filter((col) => col.accessorKey || col.accessorFn)
      .map((col) => col.header);

    const csvRows = data.map((row) => {
      return columns
        .filter((col) => col.accessorKey || col.accessorFn)
        .map((col) => {
          if (col.accessorKey) {
            return formatValue(row[col.accessorKey]);
          } else if (col.accessorFn) {
            return formatValue(col.accessorFn(row));
          } else {
            return "";
          }
        });
    });

    const csvContent = [headers, ...csvRows].map((e) => e.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "briefs_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatValue = (value) => {
    if (typeof value === "string") {
      // Wrap in quotes and escape existing quotes
      return `"${value.replace(/"/g, '""')}"`;
    }
    if (value === null || value === undefined) return "";
    return value;
  };

  const handleDownloadCSV = (brief_id) => {
    setisLoaderOpen(true);
    getPlansByBriefIdAPI(brief_id)
      .then((d) => {
        const mappedData = d.map(mapPlanCSVDownload("CONTROLLER"));
        convertToCSV(mappedData);
      })
      .catch((e) => {
        toast.error("something went wrong!");
      })
      .finally((v) => {
        setisLoaderOpen(false);
      });
  };

  const columns = useMemo(
    () => [
      {
        header: "Brief ID",
        accessorKey: "brief_id",
      },
      {
        header: "Category",
        accessorKey: "category",
      },
      {
        header: "Campaign Name",
        accessorKey: "campaign_name",
        cell: (info) => info.getValue() || "-",
      },
      {
        header: "Creation Date",
        accessorKey: "created_at",
        cell: (info) => moment(info.getValue()).format("YYYY-MM-DD"),
      },
      {
        header: "Brand",
        accessorKey: "brand_name",
      },
      {
        header: "Campaign Objective",
        accessorKey: "campaign_obj",
      },
      {
        header: "Start Date",
        accessorKey: "start_date",
        cell: (info) =>
          info.getValue() ? moment(info.getValue()).format("YYYY-MM-DD") : "-",
      },
      {
        header: "Duration",
        accessorKey: "duration",
        cell: (info) => info.getValue() || "-",
      },
      {
        header: "Brief Status",
        accessorKey: "status",
        cell: ({ row }) => <BriefStatusTag statusId={row.original.status} />,
      },
      {
        header: "Created By",
        accessorFn: (row) => `${row.first_name} ${row.last_name}`,
      },
      {
        header: "Zones",
        cell: ({ row }) => {
          const zones = Array.from(
            new Set(row.original.budgets.map((b) => b.zone_name))
          );
          return (
            <Tooltip title={zones.join(", ")}>
              <Typography
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "blue",
                }}
              >
                {zones.length}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        header: "States",
        cell: ({ row }) => {
          const states = Array.from(
            new Set(row.original.budgets.map((b) => b.state_name))
          );
          return (
            <Tooltip title={states.join(", ")}>
              <Typography
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "blue",
                }}
              >
                {states.length}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        header: "Cities",
        cell: ({ row }) => {
          const cities = Array.from(
            new Set(row.original.budgets.map((b) => b.city_name))
          );
          return (
            <Tooltip title={cities.join(", ")}>
              <Typography
                sx={{
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "blue",
                }}
              >
                {cities.length}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        header: "Budget",
        cell: ({ row }) => {
          const budget = row.original.budgets.reduce(
            (sum, b) => sum + parseFloat(b.budget || 0),
            0
          );
          return `₹${budget.toFixed(2)}`;
        },
      },
      {
        header: "Action",
        id: "actions",
        cell: ({ row }) => {
          const brief = row.original;
          return (
            <Stack direction="row" alignItems="center" gap={1}>
              <IconButton
                onClick={() => handleEdit(brief.brief_id)}
                sx={{ bgcolor: "green", color: "white" }}
                size="small"
              >
                <ModeEditIcon fontSize="15" />
              </IconButton>
              <IconButton
                onClick={() => handleDelete(brief.brief_id)}
                sx={{ bgcolor: "red", color: "white" }}
                size="small"
              >
                <DeleteIcon fontSize="15" />
              </IconButton>
              {isAllowedToDownload && brief.status === 1 && (
                <>
                  <IconButton
                    onClick={() => handleDownloadPlan(brief.brief_id)}
                    size="small"
                  >
                    <img src="/ppt.png" height={30} width={30} alt="ppt icon" />
                  </IconButton>
                  <IconButton
                    onClick={() => handleDownloadCSV(brief.brief_id)}
                    size="small"
                  >
                    <img src="/csv.png" height={30} width={30} alt="csv icon" />
                  </IconButton>
                </>
              )}
            </Stack>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: useMemo(() => {
      const start = pagination.pageIndex * pagination.pageSize;
      const end = start + pagination.pageSize;
      return (data || []).slice(start, end);
    }, [data, pagination]),
    columns,
    pageCount: Math.ceil((data?.length || 0) / pagination.pageSize),
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
  });
  return (
    <SuperAdminLayout activeLink={"/"} containerComponent="box">
      <Box>
        {/* Global Filter Input */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            variant="outlined"
            placeholder="Global Filter"
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            sx={{ maxWidth: 300 }} // optional: controls width of search box
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <CustomButton variant="contained" onClick={handleDownloadAllCSV}>
            Download CSV
          </CustomButton>
        </Stack>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              {/* Column Filters Row */}
              <TableRow>
                {table.getHeaderGroups()[0].headers.map((header) => (
                  <TableCell key={header.id}>
                    {header.column.getCanFilter() ? (
                      <TextField
                        fullWidth
                        size="small"
                        variant="standard"
                        placeholder={`Filter ${header.column.columnDef.header}`}
                        value={header.column.getFilterValue() || ""}
                        onChange={(e) =>
                          header.column.setFilterValue(e.target.value)
                        }
                      />
                    ) : null}
                  </TableCell>
                ))}
              </TableRow>

              {/* Headers Row */}
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {header.isPlaceholder ? null : (
                        <>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                          {{
                            asc: " 🔼",
                            desc: " 🔽",
                          }[header.column.getIsSorted()] ?? null}
                        </>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
          mt={2}
        >
          <CustomButton
            disabled={!table.getCanPreviousPage()}
            onClick={() => table.previousPage()}
          >
            Previous
          </CustomButton>
          <Typography>
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {table.getPageCount()}
          </Typography>
          <CustomButton
            disabled={!table.getCanNextPage()}
            onClick={() => table.nextPage()}
          >
            Next
          </CustomButton>
        </Stack>
      </Box>
      <Loader open={isLoading || isLoaderOpen} />
    </SuperAdminLayout>
  );
};

export default BriefList;
